import React, { useEffect, useMemo, useState } from "react";
import { getAdminReportsStatus } from "../../api/admin.api";
import { isUnauthorizedError } from "../../api/http";
import AdminBreadcrumbs from "../../components/admin/AdminBreadcrumbs";
import EmptyState from "../../components/ui/EmptyState";
import ErrorState from "../../components/ui/ErrorState";
import LoadingState from "../../components/ui/LoadingState";
import AdminLayout from "../../layouts/AdminLayout";
import AdminBlockingModal from "./AdminBlockingModal";
import { handleTableKeyNav } from "../../utils/tableNav";
import normalizeApiError from "../../utils/normalizeApiError";

const formatTimestamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const pad = (num) => String(num).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const statusTone = (value) => {
  const status = String(value || "").toLowerCase();
  if (status === "completed" || status === "success")
    return "bg-emerald-100 text-emerald-700";
  if (status === "pending" || status === "running")
    return "bg-amber-100 text-amber-700";
  if (status === "failed" || status === "error")
    return "bg-rose-100 text-rose-700";
  return "bg-slate-100 text-slate-500";
};

export default function AdminReportsStatus() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blockingError, setBlockingError] = useState("");

  const canGoPrev = page > 1;
  const canGoNext = total ? page * pageSize < total : items.length === pageSize;

  const fetchReports = async (nextPage = page) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAdminReportsStatus({ page: nextPage, pageSize });
      setItems(Array.isArray(response?.items) ? response.items : []);
      setPage(response?.page || nextPage);
      setTotal(response?.total ?? null);
    } catch (err) {
      if (isUnauthorizedError(err)) {
        setError(normalizeApiError(err));
        return;
      }
      const code = err?.response?.data?.code;
      if (code === "BILLING_CREDITS_EXHAUSTED") {
        setBlockingError(err?.response?.data?.message || "Credits exhausted.");
        return;
      }
      setError(normalizeApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(1);
  }, []);

  const displayItems = useMemo(() => items, [items]);

  return (
    <AdminLayout title="Admin Reports Status">
      <div className="space-y-6">
        <div className="card p-4">
          <AdminBreadcrumbs />
        </div>
        {loading ? (
          <LoadingState variant="table" rows={6} />
        ) : error ? (
          <ErrorState error={error} onRetry={fetchReports} />
        ) : displayItems.length === 0 ? (
          <EmptyState
            title="No reports available yet"
            message="There are no report status records to show."
            testId="admin-empty-reports-status"
          />
        ) : (
          <>
            <div
              className="hidden overflow-hidden rounded-lg border border-slate-200 lg:block"
              onKeyDown={handleTableKeyNav}
            >
              <table
                className="w-full text-left text-sm"
                role="table"
                aria-label="Reports status"
              >
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">ID</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {displayItems.map((report, index) => (
                    <tr
                      key={report.id || index}
                      className="border-t border-slate-100 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-amber-400"
                      tabIndex={0}
                      data-row="true"
                    >
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {report.id || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {report.name || report.title || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-semibold ${statusTone(
                            report.status
                          )}`}
                        >
                          {report.status || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {formatTimestamp(report.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {formatTimestamp(report.updatedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="space-y-3 lg:hidden">
              {displayItems.map((report, index) => (
                <div key={report.id || index} className="card p-4 text-xs text-slate-600">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-slate-900">
                      {report.name || report.title || "Report"}
                    </div>
                    <span
                      className={`rounded-full px-2 py-1 text-[10px] font-semibold ${statusTone(
                        report.status
                      )}`}
                    >
                      {report.status || "—"}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-slate-400">ID</p>
                      <p>{report.id || "—"}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Created</p>
                      <p>{formatTimestamp(report.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">Updated</p>
                      <p>{formatTimestamp(report.updatedAt)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <div className="flex gap-2">
          <button
            className="btn btn-secondary"
            onClick={() => fetchReports(page - 1)}
            disabled={!canGoPrev || loading}
          >
            Prev
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => fetchReports(page + 1)}
            disabled={!canGoNext || loading}
          >
            Next
          </button>
        </div>
      </div>
      <AdminBlockingModal
        title="Billing credits exhausted"
        message={blockingError}
        onClose={() => setBlockingError("")}
      />
    </AdminLayout>
  );
}
